import { Injectable } from '@angular/core';
import { Event } from '../imp-event.model';
import { EventProfitabilityService } from '../imp-event-analytics/event-profitability.service';

export interface SHVResult {
  suggestedHostValue: number | null;
  comparableEventsCount: number;
  averageEntryPricePaid: number;
  averageTotalPaidAttendance: number;
  qualifyingEvents: Event[];
}

@Injectable({
  providedIn: 'root'
})
export class SuggestedHostValueService {

  constructor(private profitabilityService: EventProfitabilityService) {}

  /**
   * Calculates the Suggested Host Value (SHV) for a given target host and event parameters.
   * 
   * Requirement rules for comparable events:
   * 1. Belongs to the specific Host (matching host ID or host name, case-insensitive)
   * 2. Has to be a Successful Event, graded at least a B (Financial Rating >= B)
   * 3. Similar venue capacity (No more than 20% difference: 0.80 * targetCapacity <= event.venueCapacity <= 1.20 * targetCapacity)
   * 4. Similar average entry ticket price (optional price threshold filtering if provided)
   * 
   * Formula:
   * SHV = Average entry price paid * (Average total paid attendance * 80%)
   * Example: $30 average entry price * (800 average total paid attendance * 0.80) = $19,200 SHV
   */
  calculateSHV(
    events: Event[],
    targetHostId: string | null | undefined,
    targetCapacity: number = 0,
    targetAvgPrice?: number,
    priceTolerancePct: number = 0.20
  ): SHVResult {
    if (!events || events.length === 0 || !targetHostId) {
      return {
        suggestedHostValue: null,
        comparableEventsCount: 0,
        averageEntryPricePaid: 0,
        averageTotalPaidAttendance: 0,
        qualifyingEvents: []
      };
    }

    const targetStr = String(targetHostId).trim().toLowerCase();
    const minCapacity = targetCapacity > 0 ? targetCapacity * 0.80 : 0;
    const maxCapacity = targetCapacity > 0 ? targetCapacity * 1.20 : Infinity;

    const qualifyingEvents = events.filter(e => {
      // 1. Host match (checks e.host.id, e.host.name, e.hostId, e.hostName, or e.host string)
      const { id: eventHostId, name: eventHostName } = this.getEventHostIdentifiers(e);
      const matchesHost =
        (eventHostId && String(eventHostId).trim().toLowerCase() === targetStr) ||
        (eventHostName && String(eventHostName).trim().toLowerCase() === targetStr);
      if (!matchesHost) return false;

      // 2. Similar venue capacity (No more than 20% difference if targetCapacity specified)
      if (targetCapacity > 0) {
        const cap = Number(e.venueCapacity) || 0;
        if (cap < minCapacity || cap > maxCapacity) return false;
      }

      // 3. Successful Event, graded at least a B (Financial Rating >= B)
      const isSuccessful = this.isEventGradedAtLeastB(e);
      if (!isSuccessful) return false;

      // 4. Similar average entry ticket price (if target price is specified and > 0)
      if (targetAvgPrice && targetAvgPrice > 0) {
        const eventAvgPrice = this.getEventAverageEntryPrice(e);
        const minPrice = targetAvgPrice * (1 - priceTolerancePct);
        const maxPrice = targetAvgPrice * (1 + priceTolerancePct);
        if (eventAvgPrice < minPrice || eventAvgPrice > maxPrice) {
          return false;
        }
      }

      return true;
    });

    if (qualifyingEvents.length === 0) {
      return {
        suggestedHostValue: null,
        comparableEventsCount: 0,
        averageEntryPricePaid: 0,
        averageTotalPaidAttendance: 0,
        qualifyingEvents: []
      };
    }

    // Calculate averages across qualifying comparable events
    let totalAttendanceSum = 0;
    let totalPriceSum = 0;

    qualifyingEvents.forEach(e => {
      totalAttendanceSum += this.getEventPaidAttendance(e);
      totalPriceSum += this.getEventAverageEntryPrice(e);
    });

    const count = qualifyingEvents.length;
    const averageTotalPaidAttendance = totalAttendanceSum / count;
    const averageEntryPricePaid = totalPriceSum / count;

    // Suggested Host Value Formula:
    // Average entry price paid * (Average total paid attendance * 0.80)
    const suggestedHostValue = Math.round(
      averageEntryPricePaid * (averageTotalPaidAttendance * 0.80)
    );

    return {
      suggestedHostValue,
      comparableEventsCount: count,
      averageEntryPricePaid: Math.round(averageEntryPricePaid * 100) / 100,
      averageTotalPaidAttendance: Math.round(averageTotalPaidAttendance),
      qualifyingEvents
    };
  }

  /**
   * Universal helper to resolve the most accurate host/performer name from an event object.
   */
  public static resolveHostName(event: any): string {
    if (!event) return '';

    // 1. Direct host object or string
    if (typeof event.host === 'object' && event.host !== null) {
      if (event.host.name && typeof event.host.name === 'string' && event.host.name.trim() && event.host.name.trim().toLowerCase() !== 'host') {
        return event.host.name.trim();
      }
    } else if (typeof event.host === 'string' && event.host.trim() && event.host.trim().toLowerCase() !== 'host') {
      return event.host.trim();
    }

    // 2. hostName property
    if (event.hostName && typeof event.hostName === 'string' && event.hostName.trim() && event.hostName.trim().toLowerCase() !== 'host') {
      return event.hostName.trim();
    }

    // 3. eventHost property
    if (event.eventHost && typeof event.eventHost === 'string' && event.eventHost.trim() && event.eventHost.trim().toLowerCase() !== 'host') {
      return event.eventHost.trim();
    }

    // 4. artist / artistName
    if (event.artistName && typeof event.artistName === 'string' && event.artistName.trim()) {
      return event.artistName.trim();
    }
    if (typeof event.artist === 'string' && event.artist.trim()) {
      return event.artist.trim();
    }
    if (typeof event.artist === 'object' && event.artist?.name) {
      return event.artist.name.trim();
    }

    // 5. promoterName (often shown as 'Hosted by <promoterName>')
    if (event.promoterName && typeof event.promoterName === 'string' && event.promoterName.trim()) {
      return event.promoterName.trim();
    }

    // 6. userName
    if (event.userName && typeof event.userName === 'string' && event.userName.trim()) {
      return event.userName.trim();
    }

    // 7. DJ / djName
    if (event.djName && typeof event.djName === 'string' && event.djName.trim()) {
      return event.djName.trim();
    }
    if (typeof event.DJ === 'object' && event.DJ?.name) {
      return event.DJ.name.trim();
    } else if (typeof event.DJ === 'string' && event.DJ.trim()) {
      return event.DJ.trim();
    }

    // 8. Event name extraction (e.g., 'Host: Metro Boomin', 'Performance: Metro Boomin')
    if (event.name && typeof event.name === 'string' && event.name.trim()) {
      const title = event.name.trim();
      const prefixMatch = title.match(/^(?:host|hosted by|performance|performing|starring|ft\.?|featuring)\s*:\s*(.+)$/i);
      if (prefixMatch && prefixMatch[1]) {
        return prefixMatch[1].trim();
      }
      return title;
    }

    return '';
  }

  /**
   * Helper to extract host ID and Name across different event object schemas.
   */
  public getEventHostIdentifiers(e: any): { id?: string; name?: string } {
    let id: string | undefined;
    let name: string | undefined;

    if (e) {
      if (typeof e.host === 'object' && e.host !== null) {
        id = e.host.id;
        if (e.host.name && typeof e.host.name === 'string' && e.host.name.trim() && e.host.name.trim().toLowerCase() !== 'host') {
          name = e.host.name.trim();
        }
      } else if (typeof e.host === 'string' && e.host.trim() && e.host.trim().toLowerCase() !== 'host') {
        name = e.host.trim();
        id = e.host.trim();
      }

      if (e.hostId) id = e.hostId;
      if (e.hostName && typeof e.hostName === 'string' && e.hostName.trim().toLowerCase() !== 'host') {
        name = e.hostName.trim();
      }
      if (!name) {
        name = SuggestedHostValueService.resolveHostName(e);
      }
    }

    return { id, name };
  }

  /**
   * Helper to check if an event is graded at least B (Financial Rating >= B: 'A+', 'A', or 'B').
   */
  public isEventGradedAtLeastB(e: any): boolean {
    const validGrades = ['A+', 'A', 'A-', 'B+', 'B', 'B-'];

    const isGradeAorB = (gradeVal: any): boolean => {
      if (gradeVal === null || gradeVal === undefined) return false;
      const str = String(gradeVal).trim().toUpperCase();
      return validGrades.includes(str) || str.startsWith('A') || str.startsWith('B');
    };

    // Direct property check for financial rating/grade if stored on event
    if (e.financialRating && (isGradeAorB(e.financialRating) || Number(e.financialRating) >= 3.0)) {
      return true;
    }
    if (e.financialGrade && isGradeAorB(e.financialGrade)) {
      return true;
    }
    if (e.letterGrade && isGradeAorB(e.letterGrade)) {
      return true;
    }
    if (e.overallGrade && isGradeAorB(e.overallGrade)) {
      return true;
    }

    // Compute profitability grade if not directly present
    try {
      const inputs = {
        presaleTicketsSold: e.ticketCount || 0,
        presaleTicketPrice: e.averageTicketPrice || e.price || 0,
        maleDoorTicketsSold: e.paidMaleWristbandsScanned || 0,
        maleDoorTicketPrice: e.currentMaleTicketPrice || e.doorPrice || 0,
        femaleDoorTicketsSold: e.paidFemaleWristbandsScanned || 0,
        femaleDoorTicketPrice: e.currentFemaleTicketPrice || e.doorPrice || 0,
        tablesSold: e.tableCount || 0,
        tablePrice: 0,
        venueExpenses: e.venueCosts || 0,
        marketingExpenses: (e.socialMediaAds || 0) + (e.radioAds || 0) + (e.TVAds || 0),
        djExpenses: e.DJCosts || 0,
        hostExpenses: e.hostCosts || 0,
        miscellaneousExpenses: e.otherExpenses || 0,
        preferredMargin: e.preferredProfitMargin || null
      };

      const result = this.profitabilityService.calculateProfitabilityFromPreviewInputs(inputs);
      if (result.letterGrade && isGradeAorB(result.letterGrade)) {
        return true;
      }

      // Default fallback: if total revenue exceeds expenses with positive profit margin
      const totalRev = e.totalRevenue || (e.ticketDoorRevenue || 0) + (e.ticketOrderRevenue || 0);
      const totalExp = (e.venueCosts || 0) + (e.hostCosts || 0) + (e.DJCosts || 0) + (e.socialMediaAds || 0) + (e.radioAds || 0) + (e.TVAds || 0) + (e.otherExpenses || 0);
      if (totalRev > 0 && totalRev >= totalExp) {
        return true;
      }
    } catch (err) {
      // Fallback
    }

    return false;
  }

  /**
   * Helper to compute paid attendance for an event.
   */
  public getEventPaidAttendance(e: Event): number {
    const malePaid = Number(e.paidMaleWristbandsScanned) || 0;
    const femalePaid = Number(e.paidFemaleWristbandsScanned) || 0;
    const totalPaidScanned = malePaid + femalePaid;

    if (totalPaidScanned > 0) {
      return totalPaidScanned;
    }

    if (e.totalWristbandsScanned && e.totalWristbandsScanned > 0) {
      return e.totalWristbandsScanned;
    }

    if (e.ticketCount && e.ticketCount > 0) {
      return e.ticketCount;
    }

    return 0;
  }

  /**
   * Helper to compute average entry price paid for an event.
   */
  public getEventAverageEntryPrice(e: Event): number {
    const attendance = this.getEventPaidAttendance(e);
    const revenue = (Number(e.ticketDoorRevenue) || 0) + (Number(e.ticketOrderRevenue) || 0);

    if (attendance > 0 && revenue > 0) {
      return revenue / attendance;
    }

    if (e.averageTicketPrice && e.averageTicketPrice > 0) {
      return e.averageTicketPrice;
    }

    if (e.price && e.price > 0) {
      return e.price;
    }

    if (e.doorPrice && e.doorPrice > 0) {
      return e.doorPrice;
    }

    return 0;
  }

  }


